import { useCallback, useEffect, useRef, useState } from 'react';
import { Row, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import UploadDocuments, { UploadDocumentsRef } from 'components/new-ui/composed/upload-documents/UploadDocuments';
import { H2 } from 'components/new-ui/base/typography/styles';
import Button from 'components/new-ui/base/button/Button';
import FilesTable, { FileRecord } from 'components/new-ui/composed/table/FilesTable';
import Notification from 'components/ui/base/notification/Notification';
import { BulkUploadUploadFunc } from 'components/new-ui/composed/bulkUpload/BulkUpload';
import { DocumentPresignedUrlData, GetDocumentUploadUrlParams, JobDocument } from 'store/jobs/job';
import {
    createDocument,
    deleteDocument,
    getDocumentDownloadUrl,
    getDocumentUploadUrl,
    listDocumentsByJobId,
} from 'store/jobs/client';
import { useParams } from 'react-router-dom';
import { uploadFile } from 'utils/uploadFile';
import { ListPayload } from 'core/http/response';
import { FileTypes, getFileExtensionFromMime } from 'utils/file-types';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import usePollPendingSecurityChecks from 'hooks/usePollPendingSecurityChecks';
import { S3KEY_REGEX } from 'core/utils/constants';
import { TablePaginationConfig } from 'antd/es/table';
import { getTableChangeParams } from 'utils/tableHelpers';
import { triggerBrowserDownload } from 'utils/download-utils';
import { getErrorMessage } from 'utils/getErrorMessage';

const allowedFileTypes: FileTypes[] = [FileTypes.XLS, FileTypes.XLSX, FileTypes.CSV, FileTypes.PDF];
const PAGE_SIZE = 10;
const POLL_INTERVAL_MS = 10_000;

const DocumentsTab = () => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { jobId } = useParams<{ jobId: string }>();
    const uploaderRef = useRef<UploadDocumentsRef>(null);

    const [documents, setDocuments] = useState<ListPayload<JobDocument>>();
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
    const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);
    const [isDeleteFileConfirmationModalOpen, setIsDeleteFileConfirmationModalOpen] = useState(false);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [pendingDeleteFile, setPendingDeleteFile] = useState<FileRecord | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const isUploading = uploadingCount > 0;

    const handleUploadDocumentsClick = () => {
        uploaderRef.current?.triggerUpload();
    };

    const onRequestDelete = (file: FileRecord | undefined) => {
        if (!file) return;
        setPendingDeleteFile(file);
        setIsDeleteFileConfirmationModalOpen(true);
    };

    const handleCancelDelete = () => {
        setIsDeleting(false);
        setPendingDeleteFile(null);
        setIsDeleteFileConfirmationModalOpen(false);
    };

    const handleConfirmFileDelete = async () => {
        if (!jobId || !pendingDeleteFile) {
            return;
        }
        try {
            setIsDeleting(true);
            showGlobalOverlay({ type: 'LoadingOverlay' });

            await deleteDocument(jobId, pendingDeleteFile.id);

            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Document removed',
                description: 'Document successfully removed from job',
            });

            await fetchDocuments();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Failed Deletion',
                description: errorMessage || 'Could not remove document from job',
            });
        } finally {
            hideGlobalOverlay();
            setIsDeleting(false);
            setIsDeleteFileConfirmationModalOpen(false);
            setPendingDeleteFile(null);
        }
    };

    const handlePageChange = (pagination: TablePaginationConfig) => {
        if (!jobId) return;
        listDocumentsByJobId(jobId, { ...getTableChangeParams({ pagination }) });
    };

    const fetchDocuments = useCallback(async () => {
        if (!jobId) return;

        setIsLoadingDocuments(true);
        try {
            const data = await listDocumentsByJobId(jobId, {
                page: 0,
                size: PAGE_SIZE,
                sortDirection: 'DESC',
                fileSortParameter: 'UPDATED_AT',
            });
            setDocuments(data);
            setLastFetchedAt(Date.now());
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            Notification({
                description: errorMessage || 'Could not get documents for this job',
                messageTitle: 'Documents Error',
                placement: 'top',
                type: 'error',
            });
        } finally {
            setIsLoadingDocuments(false);
        }
    }, [jobId]);

    const handleUpload = async (options: Parameters<BulkUploadUploadFunc>[0]) => {
        if (!jobId) {
            throw new Error('Missing job id');
        }

        showGlobalOverlay({ type: 'LoadingOverlay' });
        setUploadingCount((c) => c + 1);

        const fileExt = (options.file as File).name.split('.').at(-1)?.toLowerCase();

        if (
            !fileExt ||
            (Array.isArray(allowedFileTypes) && !allowedFileTypes.map((type) => type.toLowerCase()).includes(fileExt))
        ) {
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'File upload failed: File type not supported.',
                description:
                    'The system only accepts files with the extensions .xls, .xlsx, .csv, or .pdf. Please try again with a supported file',
            });
            hideGlobalOverlay();
            return;
        }

        try {
            // Get the presigned url
            const file = options.file as File;
            const presignedUrlPayload: GetDocumentUploadUrlParams = {
                documentName: file.name,
                documentSize: file.size,
            };

            const { preSignedUrl }: DocumentPresignedUrlData = await getDocumentUploadUrl(jobId, presignedUrlPayload);

            // send PUT request with the presigned url
            await uploadFile(file, preSignedUrl);

            // Extract s3 link from the presigned url
            const match = preSignedUrl.match(S3KEY_REGEX);
            const s3key = match?.[1];

            if (!s3key) {
                throw new Error('Could not extract s3key from presigned url');
            }

            // Upload the file by adding the s3 link in the payload
            await createDocument(jobId, {
                s3key,
                fileName: presignedUrlPayload.documentName,
                fileSize: presignedUrlPayload.documentSize,
                type: 'OTHER',
            });

            Notification({
                placement: 'top',
                type: 'success',
                messageTitle: 'Success',
                description:
                    'Your document has been uploaded successfully. We are preparing the file for you - this may take a while.',
            });

            fetchDocuments();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Upload Error',
                description: errorMessage || 'Could not upload file',
            });
        } finally {
            setUploadingCount((count) => Math.max(0, count - 1));
            hideGlobalOverlay();
        }
    };

    const handleDownloadFile = async (file: FileRecord | undefined) => {
        if (!file || !jobId) {
            return Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Download Error',
                description: 'Missing file or job data.',
            });
        }
        try {
            const { preSignedUrl } = await getDocumentDownloadUrl(jobId, file.id);

            const response = await fetch(preSignedUrl);
            const blob = await response.blob();
            triggerBrowserDownload(blob, file?.filename || 'document');
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            return Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Download Error',
                description: errorMessage || 'Could not download document. Please try again.',
            });
        }
    };

    const handlePreviewFile = async (file: FileRecord | undefined) => {
        if (!file || !jobId) {
            return Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Preview Error',
                description: 'Missing file or job data.',
            });
        }

        try {
            const filePreview = await getDocumentDownloadUrl(jobId, file.id);
            window.open(filePreview.preSignedUrl);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            return Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Preview Error',
                description: errorMessage || 'Could not generate preview url.',
            });
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    // Poll documents on interval so we can update the UI based on securityCheckStatus for each file
    usePollPendingSecurityChecks(documents?.list, fetchDocuments, lastFetchedAt, {
        enabled: !!jobId,
        intervalMs: POLL_INTERVAL_MS,
    });

    return (
        <div>
            <Row
                justify="space-between"
                align="middle"
                style={{
                    marginBottom: 16,
                }}
            >
                <H2>Documents</H2>
                <Button loading={isUploading} onClick={handleUploadDocumentsClick} icon={<UploadOutlined />}>
                    Upload Documents
                </Button>
            </Row>

            <UploadDocuments
                ref={uploaderRef}
                //  multiple
                // Note: There is some issue with the BE where when we create multiple documents at the same time
                // it overrides the results. Enable `multiple` when the issue is fixed
                showUploadList={false}
                customRequest={handleUpload}
            />

            <H2
                style={{
                    marginTop: 24,
                    marginBottom: 8,
                }}
            >
                Uploaded Documents
            </H2>

            <FilesTable
                loading={isLoadingDocuments}
                actions={{
                    preview: handlePreviewFile,
                    delete: onRequestDelete,
                    download: handleDownloadFile,
                }}
                onChange={handlePageChange}
                dataSource={documents?.list.map((d) => ({
                    id: d.id,
                    filename: d.fileName,
                    fileType: getFileExtensionFromMime(d.contentType) ?? '-',
                    uploadedBy: d.creator.fullName,
                    createdAt: d.createdAt,
                    securityCheckStatus: d.securityChecksStatus,
                }))}
                pagination={{
                    current: (documents?.pageNumber || 0) + 1,
                    total: documents?.total,
                    showSizeChanger: false,
                    pageSize: documents?.pageSize ?? PAGE_SIZE,
                    position: ['bottomCenter'],
                    hideOnSinglePage: true,
                }}
            />

            <ConfirmationModal
                type="error"
                title="Delete Document?"
                confirmButtonText="Delete"
                content={
                    <Typography.Text>
                        The document will be deleted and all information associated with it will be lost
                    </Typography.Text>
                }
                open={isDeleteFileConfirmationModalOpen}
                confirmationButtonLoading={isDeleting}
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmFileDelete}
            />
        </div>
    );
};

export default DocumentsTab;
